import React, { useState, useEffect } from 'react';
import { BadgeCheck, ArrowRight, ShieldCheck, AlertTriangle } from 'lucide-react';
import { Certificate, Language } from '../types';
import { verifyCertificate } from '../services/certificateStorage';

interface VerificationPortalProps {
  currentLang: Language;
}

export const VerificationPortal: React.FC<VerificationPortalProps> = ({ currentLang }) => {
  const [certInput, setCertInput] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [captchaNum1, setCaptchaNum1] = useState(0);
  const [captchaNum2, setCaptchaNum2] = useState(0);
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [captchaError, setCaptchaError] = useState(false);

  const generateCaptcha = () => {
    setCaptchaNum1(Math.floor(Math.random() * 10) + 1);
    setCaptchaNum2(Math.floor(Math.random() * 10) + 1);
    setCaptchaAnswer('');
    setCaptchaError(false);
  };

  useEffect(() => {
    generateCaptcha();
    const hash = window.location.hash;
    if (hash.includes('cert=')) {
      const match = hash.match(/cert=([^&]+)/);
      if (match && match[1]) {
        const decodedCert = decodeURIComponent(match[1]);
        setCertInput(decodedCert);
      }
    }
  }, []);
  const [verificationResult, setVerificationResult] = useState<{
    certificate: Certificate | null;
    status: 'valid' | 'revoked' | 'expired' | 'not_found';
  } | null>(null);

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!certInput.trim()) return;

    if (parseInt(captchaAnswer) !== (captchaNum1 + captchaNum2)) {
      setCaptchaError(true);
      return;
    }

    setIsSearching(true);
    setVerificationResult(null);
    setCaptchaError(false);

    try {
      const result = await verifyCertificate(certInput);
      setVerificationResult(result);
    } catch (e) {
      console.error(e);
    } finally {
      setIsSearching(false);
      generateCaptcha();
    }
  };

  return (
    <section id="verifier" className="py-24 bg-slate-50 dark:bg-slate-950 transition-colors">
      <div className="w-[90%] lg:w-[50%] max-w-none mx-auto px-6">
        
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-10 md:p-14 text-center rounded-sm">
          <div className="w-16 h-16 mx-auto flex items-center justify-center mb-6">
            <BadgeCheck className="w-12 h-12 text-slate-800 dark:text-slate-200" />
          </div>
          
          <h2 className="text-3xl font-semibold text-slate-900 dark:text-white mb-4">
            {currentLang === 'ar' ? 'التحقق من الشهادة' : currentLang === 'en' ? 'Certificate Verification' : 'Vérification de Certificat'}
          </h2>
          
          <p className="text-sm text-slate-600 dark:text-slate-400 mb-8">
            {currentLang === 'ar'
              ? 'يرجى إدخال الرقم التسلسلي للشهادة للتحقق من صحتها في السجل الرسمي المعتمد.'
              : currentLang === 'en' 
              ? 'Please enter the serial number of your certificate to verify its authenticity.' 
              : 'Veuillez saisir le numéro de série de votre certificat pour vérifier son authenticité.'}
          </p>
          
          <form onSubmit={handleVerify}>
            <input
              type="text"
              value={certInput}
              onChange={(e) => setCertInput(e.target.value)}
              placeholder="Ex: CH-2024-XXXX"
              className="w-full border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 text-center text-sm focus:outline-none focus:border-slate-500 mb-4 rounded-sm text-slate-900 dark:text-white placeholder-slate-400 font-mono"
            />
            
            <div className="mb-4">
              <label className="block text-sm text-slate-700 dark:text-slate-300 mb-2 font-medium">
                {currentLang === 'ar'
                  ? `التحقق الأمني ضد الروبوت : كم حاصل جمع ${captchaNum1} + ${captchaNum2} ؟`
                  : currentLang === 'en' 
                  ? `Anti-robot check: What is ${captchaNum1} + ${captchaNum2} ?` 
                  : `Vérification anti-robot : Combien font ${captchaNum1} + ${captchaNum2} ?`}
              </label>
              <input
                type="number"
                value={captchaAnswer}
                onChange={(e) => setCaptchaAnswer(e.target.value)}
                placeholder="?"
                className={`w-full border ${captchaError ? 'border-red-500' : 'border-slate-300 dark:border-slate-700'} bg-white dark:bg-slate-900 p-4 text-center text-sm focus:outline-none focus:border-slate-500 rounded-sm text-slate-900 dark:text-white placeholder-slate-400 font-mono`}
              />
              {captchaError && (
                <p className="text-red-500 text-xs mt-1">
                  {currentLang === 'ar'
                    ? 'إجابة خاطئة. يرجى المحاولة مرة أخرى.'
                    : currentLang === 'en' 
                    ? 'Incorrect answer. Please try again.' 
                    : 'Réponse incorrecte. Veuillez réessayer.'}
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={isSearching || !certInput.trim()}
              className="w-full bg-slate-900 dark:bg-white text-white dark:text-slate-900 p-4 text-sm font-semibold flex items-center justify-center gap-2 hover:bg-slate-800 dark:hover:bg-slate-200 disabled:opacity-50 transition-colors rounded-sm"
            >
              {isSearching 
                ? (currentLang === 'ar' ? 'جاري التحقق...' : currentLang === 'en' ? 'Verifying...' : 'Vérification...') 
                : (currentLang === 'ar' ? 'تحقق الآن' : currentLang === 'en' ? 'Verify' : 'Vérifier')
              } 
              <ArrowRight className="w-4 h-4 rtl:rotate-180" />
            </button>
          </form>

          {/* Minimalist Results Area */}
          {verificationResult && (
            <div className="mt-8 pt-8 border-t border-slate-200 dark:border-slate-800 text-left rtl:text-right">
              {verificationResult.status === 'valid' && verificationResult.certificate ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-emerald-600 dark:text-emerald-400 mb-4">
                    <ShieldCheck className="w-5 h-5 shrink-0" />
                    <span className="font-semibold text-sm">
                      {currentLang === 'ar' ? 'شهادة أصلية وصالحة معتمدة' : currentLang === 'en' ? 'Authentic & Valid Certificate' : 'Certificat Authentique & Valide'}
                    </span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                    <div className="text-slate-500">
                      {currentLang === 'ar' ? 'المعرف الرقمي' : currentLang === 'en' ? 'Certificate ID' : 'Identifiant'}
                    </div>
                    <div className="text-slate-900 dark:text-white font-mono font-medium">
                      {verificationResult.certificate.id}
                    </div>
                    
                    <div className="text-slate-500">
                      {currentLang === 'ar' ? 'المستفيد / المؤسسة' : currentLang === 'en' ? 'Recipient / Organization' : 'Bénéficiaire / Entreprise'}
                    </div>
                    <div className="text-slate-900 dark:text-white font-medium">
                      {verificationResult.certificate.recipientName}
                      {verificationResult.certificate.recipientCompany && ` (${verificationResult.certificate.recipientCompany})`}
                    </div>
                    
                    <div className="text-slate-500">
                      {currentLang === 'ar' ? 'عنوان الشهادة والاعتماد' : currentLang === 'en' ? 'Certificate Title' : 'Intitulé du Certificat'}
                    </div>
                    <div className="text-slate-900 dark:text-white font-medium">
                      {verificationResult.certificate.courseTitle[currentLang] || verificationResult.certificate.courseTitle['fr']}
                    </div>

                    <div className="text-slate-500">
                      {currentLang === 'ar' ? 'تاريخ الإصدار' : currentLang === 'en' ? 'Issue Date' : 'Date de Délivrance'}
                    </div>
                    <div className="text-slate-900 dark:text-white font-medium">
                      {new Date(verificationResult.certificate.issueDate).toLocaleDateString(currentLang === 'ar' ? 'ar-DZ' : currentLang === 'en' ? 'en-US' : 'fr-FR')}
                    </div>

                    <div className="text-slate-500">
                      {currentLang === 'ar' ? 'تاريخ انتهاء الصلاحية' : currentLang === 'en' ? 'Expiry Date' : 'Date de Validité'}
                    </div>
                    <div className="text-slate-900 dark:text-white font-medium">
                      {verificationResult.certificate.expiryDate 
                        ? new Date(verificationResult.certificate.expiryDate).toLocaleDateString(currentLang === 'ar' ? 'ar-DZ' : currentLang === 'en' ? 'en-US' : 'fr-FR')
                        : (currentLang === 'ar' ? 'دائمة (مدى الحياة)' : currentLang === 'en' ? 'Lifetime (Permanent)' : 'À vie (Permanent)')}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center gap-3 text-red-600 dark:text-red-400 py-4">
                  <AlertTriangle className="w-8 h-8" />
                  <span className="font-semibold text-sm text-center">
                    {currentLang === 'ar' 
                      ? 'الشهادة غير موجودة أو غير صالحة في السجل الرسمي' 
                      : currentLang === 'en' 
                      ? 'Certificate not found or invalid in official registry' 
                      : 'Certificat introuvable ou invalide dans le registre officiel'}
                  </span>
                </div>
              )}
            </div>
          )}

        </div>

      </div>
    </section>
  );
};
